<template>
    <div>
        <div v-if="isReadonly" class="mb-2">
            <span>Preview</span>
            <layers-header-list-preview />
        </div>
        <c-resizable class="layers-container">
            <template #header>
                <layers-header
                    :start-with-canva="startWithCanva"
                    :title="settings.title"
                    @import-layers="importLayers"
                    @user-updated="() => emit('user-updated')"
                    @open-find-layers="openFindLayers"
                />
            </template>

            <find-in-layers ref="findInLayersRef" :enable="enableFindInLayers" @layer-selected="setActiveElement" />

            <layers-tree class="layers-tree" @tree-item-moved="layerTreeItemMove" />
        </c-resizable>
    </div>
</template>

<script lang="ts" setup>
import { useLayers } from './composables/useLayers';
import store from '@/store';

import type { LayersSettings, LayersFlags, LayersTemplate } from './types';
import type { CanvasEx } from '@/typings/canvas';

import LayersTree from './tree/LayersTree.vue';
import { FindInLayersRef } from './header';
import LayersHeader from './header/LayersHeader.vue';
import FindInLayers from './header/FindInLayers.vue';
import CResizable from '@/components/shared/CResizable.vue';
import LayersHeaderListPreview from '@/components/Layers/header/list-preview/LayersHeaderListPreview.vue';
import { computed, onUnmounted, Ref, ref } from 'vue';

const emit = defineEmits<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: 'import-layers', value: any): void;
    (event: 'tree-item-moved'): void;
    (event: 'user-updated'): void;
}>();

const props = withDefaults(
    defineProps<{
        settings: LayersSettings;
        flags: LayersFlags;
        canvas: CanvasEx | null;
        auxCanvas?: CanvasEx | null;
        template?: LayersTemplate;
    }>(),
    {
        flags: () => ({
            allowSorting: true,
            allowChangingPrintArea: true,
            allowChangingOverlay: true,
            showGroups: true,
            isAbleToGroupOrUnGroup: true,
            enablePageEps: true,
            isAdminTemplateProduct: false,
            startWithCanva: false,
            enableFindInLayers: true
        })
    }
);

const flags = computed(() => {
    return store.state.storeLayers?.flags;
});

const isReadonly = computed(() => {
    return flags.value?.readonly;
});

const startWithCanva = computed(() => {
    return flags.value?.startWithCanva;
});

const enableFindInLayers = computed(() => {
    return flags.value?.enableFindInLayers;
});

const {
    setActiveElement,
    singleGroupSelection,
    initGroupSelectionFromPageEps,
    importLayers,
    refreshLayerPanel,
    moveElementForward,
    moveElementBackward,
    bringElementToFront,
    sendElementToBack
} = useLayers(props, emit);

defineExpose({
    setActiveElement,
    singleGroupSelection,
    initGroupSelectionFromPageEps,
    refreshLayerPanel,
    moveElementForward,
    moveElementBackward,
    bringElementToFront,
    sendElementToBack
});

const findInLayersRef: Ref<FindInLayersRef | null> = ref(null);

onUnmounted(() => {
    store.dispatch('storeLayers/clear');
});

const layerTreeItemMove = () => {
    emit('tree-item-moved');
};

const openFindLayers = () => {
    findInLayersRef?.value?.toggle();
};
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/bootstrap.scss';

::v-deep .c-button-wrapper.btn.dropdown-toggle.btn-empty.btn-md {
    color: #87acd5 !important;
    font-weight: 700 !important;
    &:focus {
        border: 1.5px solid #87acd5 !important;
        border-radius: 4px;
    }
}

::v-deep .dropdown-menu.show {
    position: absolute;
    transform: translate3d(-174px, 38px, 0px) !important;
    top: 0px !important;
    left: 0px !important;
    will-change: transform !important;
}

::v-deep .dropdown-item {
    &:active {
        background-color: transparent !important;
        color: black !important;
    }
}

::v-deep .layers-tree {
    --primary-blue: #87add5;
    --secondary-color: #a7aab4;
    --secondary-hover-color: #868686;
}

::v-deep .cursor-pointer {
    cursor: pointer;
}

::v-deep .items-center {
    align-items: center;
}

::v-deep .w-full {
    width: 100%;
}

::v-deep .grow-1 {
    flex-grow: 1;
}

::v-deep .shrink {
    flex-shrink: 1;
}
</style>
